import {Injectable,} from '@angular/core';
import {SystemConstant,} from '@services/constant/system-constant';
import {StringUtil,} from '@services/util/string-util';
import {Decimal,} from 'decimal.js';
import {CastUtil,} from '@services/util/cast-util';
import {Logger,} from '@services/log/logger';

/**
 * データ型の変更ユーティリティー
 */
@Injectable({
  providedIn: 'root',
})
export class ChangeTypeUtil {
  constructor(private logger: Logger, private stringUtil: StringUtil, private castUtil: CastUtil) {}

  /**
   * [一般開発者利用可]
   * NothingをNullに変換
   * @param {Date} val 日付データ型
   * @returns パラメータが作成した最小日付以外の場合、渡された値を返す。/ それ以外の場合、nullを返す。
   */
  nothingToDBNullDate(val: Date | null | undefined): Date | null {
    //それ以外の場合、nullを返す。
    if (val == null) {
      return null;
    }
    //パラメータが作成した最小日付以外の場合、渡された値を返す。
    if (new Date(SystemConstant.DATE_MIN_VALUE).getTime() !== new Date(val).getTime()) {
      return val;
    } else {
      return null;
    }
  }

  /**
   * [一般開発者利用可]
   * NothingをNullに変換
   * @param {string} val 文字列型
   * @returns 渡される値がNull、又はエンプティーの場合、Nullを返す。逆の場合、それ自体を返す。
   */
  nothingToDBNullString(val: string | null | undefined): string | null {
    //渡される値がNull、又はエンプティーであるかをチェックする。
    if (val == null || val === SystemConstant.EMPTY) {
      return null;
    } else {
      return val.toString();
    }
  }

  /**
   * [一般開発者利用可]
   * NothingをNullに変換(各種ID)
   * @param {Decimal} val decimalデータ型
   * @returns 渡される値が0の場合、Nullを返す。それ以外の場合、それ自体を返す。
   */
  nothingDecimalToDBNullForIdString(val: Decimal | null | undefined): Decimal | null {
    //渡される値が存在するかチェックする。
    if (val == null) {
      return null;
    }

    //渡される値が0の場合、Nullを返す。それ以外の場合、それ自体を返す。
    if (Number(val) !== 0) {
      return val;
    } else {
      return null;
    }
  }

  /**
   * [一般開発者利用可]
   * NULLをNothingに変換
   * @param {number} val Number型
   * @returns nullの場合、0を返す。逆の場合、それ自体を返す。
   */
  nullToNumber(val: number | null | undefined): number {
    // 入力パラメータがnulldの場合、0を返す。
    if (val == null) {
      return 0;
    } else {
      // Numberに変換する。
      return val;
    }
  }

  /**
   * [一般開発者利用可]
   * NULLをNothingに変換
   * @param {number} val Number型
   * @returns nullの場合、0を返す。逆の場合、Decimalに変換した値を返す。
   */
  nullToLong(val: number | null | Decimal | undefined): Decimal {
    // 入力パラメータがnullの場合、0を返す。
    if (val == null) {
      return new Decimal(0);
    } else {
      // Decimalに変換する。
      return this.castUtil.parseInteger(val.toString());
    }
  }

  /**
   * [一般開発者利用可]
   * NULLを空文字に変換
   * @param {string} val String型
   * @returns nullの場合、空白（""）を返す。逆の場合、Stringに変換した値を返す。
   */
  nullToString(val: string | null | undefined): string {
    // 入力パラメータがnullの場合、空白（""）を返す。
    if (val == null) {
      return SystemConstant.EMPTY;
    } else {
      // Stringに変換する。
      return val.toString();
    }
  }

  /**
   * [一般開発者利用可]
   * NULLをNothingに変換
   * @param {number} val Object型
   * @returns objectからDecimalに変換し、nullの場合、0を返す。逆の場合、Decimalに変換した値を返す。
   */
  nullToNothing(val: number | null | Decimal | undefined): Decimal {
    // 入力パラメータがnullの場合、0を返す。
    if (val == null) {
      return new Decimal(0);
    } else {
      // Decimalに変換する。
      return this.castUtil.parseInteger(val.toString());
    }
  }

  /**
   * [一般開発者利用可]
   * NULLを空文字に変換
   * @param {Date} val 日付データ型
   * @returns 入力値がNullの場合、最小日付を返す。逆の場合、その値自体を返す。
   */
  nullToDate(val: Date | null | undefined): Date {
    if (val != null) {
      return val;
    } else {
      return this.castUtil.parseDate(SystemConstant.DATE_MIN_VALUE, SystemConstant.DATE_FORMAT_YYYYMMDDHHMMSS);
    }
  }

  /**
   * [一般開発者利用可]
   * NULLをNothingに変換
   * @param {Decimal} val decimalデータ型
   * @returns 入力値がNullの場合、0を返す。逆の場合、その値自体を返す。
   */
  nullToDecimal(val: Decimal | null | undefined): Decimal {
    if (val == null) {
      return new Decimal(0);
    } else {
      return val;
    }
  }

  /**
   * [一般開発者利用可]
   * NULLをNothingに変換
   * @param {number} val numberデータ型
   * @returns 入力値がNullの場合、0を返す。逆の場合、その値自体を返す。
   */
  nullToInteger(val: number | null): number {
    if (val == null) {
      return 0;
    } else {
      return this.castUtil.parseNumber(val.toString());
    }
  }

  /**
   * [一般開発者利用可]
   * NothingをNullに変換(各種ID)
   * @param {number} value numberデータ型
   * @returns 渡される値が0の場合、Nullを返す。それ以外の場合、それ自体を返す。
   */
  nothingIntToDBNullForIdString(value: number | null | undefined): number | null {
    // Nullまたは0の場合、エンプティを返す。それ以外の場合、その値自体を返す。
    if (value == null) {
      return null;
    } else {
      return value;
    }
  }

  /**
   * [一般開発者利用可]
   * Nothingを"0"に変換
   * @param {number} value 文字列型
   * @returns 入力値がNullの場合は0を返す。それ以外の場合はその入力値の自体を返す。
   */
  nothingToDBNullSingle(value: number | null | undefined): number {
    // 入力値がNullの場合は0を返す。それ以外の場合はその入力値の自体を返す。
    if (value != null) {
      return Number(value.toFixed(6));
    } else {
      return 0;
    }
  }

  /**
   * [一般開発者利用可]
   * NothingをNullに変換(各種ID)
   * @param {number} val numberデータ型
   * @returns 渡される変数が0の場合、エンプティを返す。それ以外の場合、渡される変数自体を返す。
   */
  nothingLongToDBNullForIdString(val: number | null): number | null {
    // 渡される変数が0の場合、エンプティを返す。それ以外の場合、渡される変数自体を返す。
    if (val == null) {
      return null;
    } else {
      return val;
    }
  }

  /**
   * [一般開発者利用可]
   * NothingをNullに変換
   * @param {Decimal} val Decimalデータ型
   * @returns 渡される変数が0の場合、エンプティを返す。それ以外の場合、渡される変数自体を返す。
   */
  nothingToDBNullDecimal(val: Decimal | null | undefined): Decimal | null {
    // 渡される変数が0の場合、エンプティを返す。それ以外の場合、渡される変数自体を返す。
    if (val == null || new Decimal(0).comparedTo(val) === 0) {
      return null;
    } else {
      return val;
    }
  }

  /**
   * [一般開発者利用可]
   * Nothingを"0"に変換
   * @param {number} val Number型
   * @returns NULLまたはUndefinedの場合、0を返す。それ以外の場合、それ自体を返す。
   */
  nothingToDBNullInt(val: number | undefined | null): number {
    if (val == null) {
      // 入力パラメータがNULLまたはUndefinedの場合、0を返す。
      return 0;
    } else {
      // Numberに変換する。
      return val;
    }
  }

  /**
   * [一般開発者利用可]
   * カタカナ変換（小文字(ァ,ィ,ゥ,ェ,ォ,ッ,ャ,ュ,ョ)⇒大文字(ア,イ,ウ,エ,オ,ツ,ヤ,ユ,ヨ)）
   * @param {string} val 編集文字
   * @returns 編集後の文字列
   */
  changeKatakanaUpperCase(val: string): string {
    let returnValue = SystemConstant.EMPTY;

    if (!this.stringUtil.isEmpty(val)) {
      // 渡される引数の値を設定する
      returnValue = val;
      // 小文字⇒大文字
      returnValue = returnValue.replaceAll('ァ', 'ア');
      returnValue = returnValue.replaceAll('ィ', 'イ');
      returnValue = returnValue.replaceAll('ゥ', 'ウ');
      returnValue = returnValue.replaceAll('ェ', 'エ');
      returnValue = returnValue.replaceAll('ォ', 'オ');
      returnValue = returnValue.replaceAll('ッ', 'ツ');
      returnValue = returnValue.replaceAll('ャ', 'ヤ');
      returnValue = returnValue.replaceAll('ュ', 'ユ');
      returnValue = returnValue.replaceAll('ョ', 'ヨ');

      // ログに書き込むために処理後の文字列を格納する。
      let strAppend = SystemConstant.EMPTY;
      strAppend += 'ChangeKatakanaUpperCase';
      strAppend += ' 変換前：';
      strAppend += val;
      strAppend += ' 変換後：';
      strAppend += returnValue;
      this.logger.debug(strAppend);
    }
    return returnValue;
  }

  /**
   * [一般開発者利用可]
   * Nothingを0に変換(Long)
   * @param val 文字列型
   * @returns  値がNullの場合、0を返す。それ以外の場合、それ自体を返す。
   */
  nothingToDBNull(val: Decimal | null): Decimal {
    if (val != null) {
      return val;
    } else {
      return new Decimal(0);
    }
  }

  /**
   * [一般開発者利用可]
   * 空文字をNullに変換
   *
   * @param val string型
   * @returns NULLまたはEMPTYの場合、NULLを返す。それ以外の場合、それ自体を返す。
   */
  stringToNull(val: string | null | undefined): string | null {
    if (val == null || val === SystemConstant.EMPTY) {
      return null;
    } else {
      return val;
    }
  }

  /**
   * [一般開発者利用可]
   * TextBoxへの数値入力をDecimal型として返却。空文字であった場合、0を返却
   *
   * @param val string型
   * @returns 数値を返します
   */
  toDecimal(val: string): Decimal {
    if (!(this.stringUtil.isNumeric(val) && val.length > 0)) {
      this.logger.debug('TextBoxへの数値入力が空欄であるため、0を返却します。');
      return new Decimal(0);
    }
    //Decimal返却
    return new Decimal(this.castUtil.parseDecimal(val).toFixed(0));
  }

  /**
   * [一般開発者利用可]
   * Nothingを空文字に変換
   *
   * @param val string型
   * @returns 値がNullの場合、空欄（''）を返す。それ以外の場合、それ自体を返す。
   */
  nothingToString(val: string | undefined | null): string {
    if (val == null) {
      return SystemConstant.EMPTY;
    } else {
      return val;
    }
  }

  /**
   * [一般開発者利用可]
   *
   * @param val string型
   * @returns NULLまたはundefinedまたはbooleanの場合、Trueを文字列として返す。それ以外の場合、Falseを文字列として返す
   */
  nothingBooleanToString(val: boolean | undefined | null): string {
    if (val) {
      return 'True';
    } else {
      return 'False';
    }
  }
}
